import React, { cloneElement, useEffect, useState } from 'react'
import SmallModal from './SmallModal'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded'
import { Modal } from '@mui/material'
import { CloudDone } from '@mui/icons-material'

const ErrorModal = ({
  statusCode,
  message,
  open,
  onClose,
  noAutoClose,
  title,
  icon,
  button,
  success,
}) => {
  const [count, setCount] = useState(10)

  useEffect(() => {
    if (!open) {
      setCount(10)
      return
    }

    if (noAutoClose) return

    if (count === 0) {
      onClose?.()
      return
    }

    const timer = setTimeout(() => {
      setCount((prev) => prev - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [count, open, onClose])

  const progress = (count / 10) * 100

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          mx: 'auto',
          borderRadius: 4,
          bgcolor: 'background.paper',
          px: 3,
          py: 3,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: success
                ? 'rgba(37, 227, 50, 0.08)'
                : 'rgba(227, 37, 37, 0.08)',
              color: success ? 'success.main' : 'error.main',
              animation: open ? 'softPulse 1.8s ease-in-out infinite' : 'none',
              '@keyframes softPulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: success
                    ? '0 0 0 0 rgba(37, 227, 50, 0.18)'
                    : '0 0 0 0 rgba(227, 37, 37, 0.18)',
                },
                '70%': {
                  transform: 'scale(1.03)',
                  boxShadow: success
                    ? '0 0 0 10px rgba(37, 227, 50, 0)'
                    : '0 0 0 10px rgba(227, 37, 37, 0)',
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: success
                    ? '0 0 0 0 rgba(37, 227, 50, 0)'
                    : '0 0 0 0 rgba(227, 37, 37, 0)',
                },
              },
            }}
          >
            {cloneElement(
              icon || (success ? <CloudDone /> : <ErrorOutlineRounded />),
              {
                sx: {
                  fontSize: 36,
                  ...(icon?.props?.sx || {}),
                },
              },
            )}
          </Box>

          <Stack spacing={0.75}>
            <Typography variant="h6" fontWeight={700}>
              {title || (success ? 'Success' : 'Something went wrong')}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 320 }}
            >
              {message ||
                (success
                  ? 'Action Successfully Completed'
                  : 'An unexpected error occurred.')}
            </Typography>

            {statusCode && (
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-flex',
                  alignSelf: 'center',
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  bgcolor: 'rgba(15,23,42,0.05)',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                Status code: {statusCode}
              </Typography>
            )}
          </Stack>

          {!noAutoClose && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: success ? 'success.light' : 'error.light',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: success ? 'success.main' : 'error.main',
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Closing in {count}s
              </Typography>
            </Box>
          )}

          {button && button}

          {onClose && (
            <Stack direction="row" spacing={1.25} sx={{ width: '100%' }}>
              <Button
                onClick={onClose}
                variant="outlined"
                color={success ? 'success' : 'error'}
                fullWidth
              >
                {!noAutoClose ? 'Close now' : 'Close'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  )
}

export default ErrorModal
